import { canonicalRelationshipTypeForPersist } from "./enrichmentRelationshipPersist";
import { augmentRelMissingEndpoint } from "./enrichmentMissingEndpoint";

function normName(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

export function normalizeTypeForCompare(t) {
  return String(t || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/_/g, "-");
}

function enrichmentAnchorContext(preview, editingEntity = null) {
  const anchorId = String(
    preview?.anchor_application_id || editingEntity?.id || ""
  ).trim();
  const anchorName = normName(
    (typeof editingEntity?.name === "string" && editingEntity.name.trim()) ||
      preview?.anchor_name ||
      ""
  );
  const anchorType = normalizeTypeForCompare(
    editingEntity?.type ||
      editingEntity?.entity_type ||
      preview?.anchor_entity_type ||
      "application"
  );
  return { anchorId, anchorName, anchorType };
}

/** Mirror orchestrator rel_type_for — compound labels for application-centric associations. */
export const ENRICHMENT_PEER_COMPOUND_TYPE = {
  organization: "organization uses application",
  "business-capability": "application supports business-capability",
  "business-context": "application supports business-context",
  initiative: "initiative affects application",
  platform: "application runs on platform",
  "data-object": "application accesses data-object",
  "technology-component": "application uses technology-component",
};

const INCOMING_PEER_ON_APPLICATION = new Set([
  "organization",
  "initiative",
  "platform",
]);

function normRelType(rt) {
  return canonicalRelationshipTypeForPersist(rt) || String(rt || "").trim().toLowerCase();
}

export function enrichmentCompoundTypeForAppAssociation(peerType, anchorIsSource) {
  const peer = normalizeTypeForCompare(peerType);
  if (!peer) return null;
  if (anchorIsSource && !INCOMING_PEER_ON_APPLICATION.has(peer)) {
    return ENRICHMENT_PEER_COMPOUND_TYPE[peer] || null;
  }
  if (!anchorIsSource && INCOMING_PEER_ON_APPLICATION.has(peer)) {
    return ENRICHMENT_PEER_COMPOUND_TYPE[peer] || null;
  }
  return null;
}

/** Flip source/target ends (ids, surfaces, missing metadata) when metamodel direction changes. */
export function flipProposedRelationshipEndpoints(rel) {
  if (!rel || typeof rel !== "object") return rel;
  const r = augmentRelMissingEndpoint({ ...rel });
  const sid = r.source_entity_id;
  const tid = r.target_entity_id;
  const out = { ...r };

  out.source_entity_id = tid || null;
  out.target_entity_id = sid || null;

  const sSurf = r.surface_source_name;
  const tSurf =
    r.surface_target_name || r.surface_other_application || r.surface_child_application;
  out.surface_source_name = tSurf || null;
  out.surface_target_name = sSurf || null;
  out.surface_other_application = r.surface_target_name ? null : r.surface_other_application;
  out.surface_child_application = r.surface_child_application;

  const sType = r.source_entity_type;
  const tType = r.target_entity_type;
  out.source_entity_type = tType || null;
  out.target_entity_type = sType || null;

  if (r.missing_end === "source") {
    out.missing_end = "target";
  } else if (r.missing_end === "target") {
    out.missing_end = "source";
  }

  return augmentRelMissingEndpoint(out);
}

/**
 * Choose display/persist relationship_type after metamodel resolution (canonical from API).
 */
export function displayRelationshipTypeAfterMetamodel(
  resolvedCanonical,
  sourceType,
  targetType,
  rel,
  preview,
  editingEntity
) {
  const resolved = String(resolvedCanonical || "").trim();
  if (!resolved) return resolved;
  const { anchorId, anchorType } = enrichmentAnchorContext(preview, editingEntity);
  const r = augmentRelMissingEndpoint(rel);
  const sid = String(r.source_entity_id || "").trim();
  const tid = String(r.target_entity_id || "").trim();
  const anchorOnSource = Boolean(anchorId && sid && sid === anchorId);
  const anchorOnTarget = Boolean(anchorId && tid && tid === anchorId);
  const anchorOnMissingSource =
    !sid && r.missing_end === "source" && normalizeTypeForCompare(r.missing_entity_type) === anchorType;
  const anchorOnMissingTarget =
    !tid && r.missing_end === "target" && normalizeTypeForCompare(r.missing_entity_type) === anchorType;

  const peerType = anchorOnSource || anchorOnMissingTarget
    ? targetType
    : anchorOnTarget || anchorOnMissingSource
      ? sourceType
      : null;
  const anchorIsSource = anchorOnSource || anchorOnMissingTarget;
  const compound = peerType
    ? enrichmentCompoundTypeForAppAssociation(peerType, anchorIsSource)
    : null;
  return compound || resolved;
}

/**
 * Client-side guess for relationship_type before the reconcile API returns.
 * Uses application-centric compound labels when the peer type matches orchestrator patterns.
 */
export function guessInstantRelationshipType(rel, sourceType, targetType, preview, editingEntity, flipped) {
  const base = flipped ? flipProposedRelationshipEndpoints(rel) : rel;
  const row = augmentRelMissingEndpoint(base);
  const { anchorId, anchorType } = enrichmentAnchorContext(preview, editingEntity);
  const sid = String(row.source_entity_id || "").trim();
  const tid = String(row.target_entity_id || "").trim();
  const st = normalizeTypeForCompare(sourceType);
  const tt = normalizeTypeForCompare(targetType);

  const anchorOnSource = Boolean(anchorId && sid && sid === anchorId);
  const anchorOnTarget = Boolean(anchorId && tid && tid === anchorId);
  const anchorOnMissingSource =
    !sid &&
    row.missing_end === "source" &&
    normalizeTypeForCompare(row.missing_entity_type) === anchorType;
  const anchorOnMissingTarget =
    !tid &&
    row.missing_end === "target" &&
    normalizeTypeForCompare(row.missing_entity_type) === anchorType;

  const peerType = anchorOnSource || anchorOnMissingTarget
    ? tt
    : anchorOnTarget || anchorOnMissingSource
      ? st
      : st && tt !== anchorType
        ? tt
        : st;
  const anchorIsSource = anchorOnSource || anchorOnMissingTarget;
  const compound = peerType
    ? enrichmentCompoundTypeForAppAssociation(peerType, anchorIsSource)
    : null;
  if (compound) return compound;

  const hint = canonicalRelationshipTypeForPersist(row.relationship_type) || row.relationship_type;
  return hint || "uses";
}

/** Whether anchor/target direction should flip when the peer catalog type changes. */
export function shouldFlipRelationshipForPeerType(rel, peerTypeNorm) {
  const incoming = INCOMING_PEER_ON_APPLICATION.has(peerTypeNorm);
  const r = augmentRelMissingEndpoint(rel);
  if (r.missing_end === "target") return incoming;
  if (r.missing_end === "source") return !incoming;
  const rt = String(r.relationship_type || "").trim();
  if (rt === "organization uses application" || rt === "initiative affects application") {
    return !incoming;
  }
  if (
    rt === "application uses technology-component" ||
    rt === "application accesses data-object"
  ) {
    return incoming;
  }
  return false;
}

export function relationshipTypeChangedForMetamodel(beforeType, afterType) {
  const a = normRelType(beforeType);
  const b = normRelType(afterType);
  if (a && b && a === b) return false;
  return String(beforeType || "").trim() !== String(afterType || "").trim();
}

/**
 * Apply resolved types to a proposed relationship row (types on ends + relationship_type).
 */
export function applyMetamodelReconcileToRelationship(
  rel,
  {
    sourceType,
    targetType,
    relationshipType,
    preview,
    editingEntity,
    flipped = false,
  }
) {
  let row = flipped ? flipProposedRelationshipEndpoints(rel) : { ...rel };
  row = augmentRelMissingEndpoint(row);
  const { anchorId } = enrichmentAnchorContext(preview, editingEntity);
  const sid = String(row.source_entity_id || "").trim();
  const tid = String(row.target_entity_id || "").trim();
  const st = normalizeTypeForCompare(sourceType);
  const tt = normalizeTypeForCompare(targetType);

  const rt = displayRelationshipTypeAfterMetamodel(
    relationshipType,
    st,
    tt,
    row,
    preview,
    editingEntity
  );

  const next = { ...row, relationship_type: rt };
  if (anchorId && sid === anchorId) {
    next.source_entity_type = st || next.source_entity_type;
    if (tt) next.target_entity_type = tt;
  } else if (anchorId && tid === anchorId) {
    next.target_entity_type = tt || next.target_entity_type;
    if (st) next.source_entity_type = st;
  } else if (row.missing_end === "source" && st) {
    next.missing_entity_type = sourceType;
    next.source_entity_type = st;
  } else if (row.missing_end === "target" && tt) {
    next.missing_entity_type = targetType;
    next.target_entity_type = tt;
  } else {
    if (st) next.source_entity_type = st;
    if (tt) next.target_entity_type = tt;
  }
  return augmentRelMissingEndpoint(next);
}
